// ── ReferralSourcesPage — dealflow origins (list) ──
import { useState, useEffect } from "react";
import { localFilter, exportCSV } from "../../../shared/format";
import { B } from "../../../shared/theme";
import { Badge, Btn, Card, ErrorMsg, ExportBtn, FilterSelect, Icon, PageLink, Pagination, STh, SearchBar, SectionHeader, Spinner, Td, TdNum, Th } from "../../../shared/ui";
import { useSortable, usePagination } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { RS_OPTIONS } from "../forms/ReferralSourceForm";
import { ReleaseReferralSourceModal, canRelease } from "../components/ReleaseReferralSource";

// ── Grid edit ──
// The columns a grid can sensibly hold. Street, website, phone types and notes
// stay in the full form: they are long, rarely bulk-changed, and a grid that
// carried all of them would be a spreadsheet nobody can read. The server
// (saveReferralSourcesBulk) only accepts this same list, and only writes the
// fields that changed — so nothing outside the grid can be blanked by it.

// Fields the "Set for selected" bar can apply to many rows at once. Names and
// emails are per-record by nature; these are the classifications that change
// in batches (a broker shop re-rated, a region reorganised, a list retired).
const BULK_FIELDS = [
  { key: "status",          label: "Status" },
  { key: "rating",          label: "Rating" },
  { key: "contact_type",    label: "Type" },
  { key: "contact_subtype", label: "Sub-Type" },
  { key: "region",          label: "Region" },
  { key: "owner",           label: "Owner", adminOnly: true },
];

const cellCss = {
  width: "100%", boxSizing: "border-box", padding: "4px 6px", fontSize: 12,
  border: "1px solid var(--color-border-secondary)", borderRadius: 4,
  background: "var(--color-background-primary)", color: "var(--color-text-primary)",
};
const CellInput = ({ value, onChange, w = 120, list }) => (
  <input value={value ?? ""} onChange={e => onChange(e.target.value)} list={list}
         style={{ ...cellCss, minWidth: w }} />
);
// A picklist that still shows a value already in the data but missing from the
// list (an old import, a value set in SQL). Without this the select would show
// the first option and a save would quietly overwrite the real value.
const CellSelect = ({ value, onChange, options, w = 110 }) => {
  const v = value ?? "";
  const has = options.some(o => o.value === v);
  const all = has ? options : [...options, { value: v, label: v ? v + " (current)" : "" }];
  return (
    <select value={v} onChange={e => onChange(e.target.value)} style={{ ...cellCss, minWidth: w }}>
      {all.map(o => <option key={o.value} value={o.value}>{o.label === "" ? "—" : o.label}</option>)}
    </select>
  );
};

export const ReferralSourcesPage = () => {
  const { api, role, go, rowNav, setModal, setConfirm, delRec, currentUser } = useApp();
  const [releaseRec, setReleaseRec] = useState(null);
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true); const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  // Status defaults to Active: an inactive source is a relationship that has
  // gone quiet, and it shouldn't be in the way of the list you work every day.
  // Still one click to see everything.
  const [statusF, setStatusF] = useState("Active");
  const [typeF, setTypeF] = useState("");
  const [ratingF, setRatingF] = useState("");
  const [regionF, setRegionF] = useState("");
  // Owner filter. "__none" = Unassigned: sources a rep released, waiting for an
  // admin to reassign. Only meaningful to someone who sees other people's.
  const [ownerF, setOwnerF] = useState("");

  // Grid-edit state. edits holds only what the user typed, keyed by id, so
  // paging, sorting and filtering never lose an unsaved change.
  const [gridOn, setGridOn] = useState(false);
  const [edits, setEdits] = useState({});
  const [rowErrs, setRowErrs] = useState({});
  const [sel, setSel] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [gridMsg, setGridMsg] = useState(null);
  const [users, setUsers] = useState([]);
  const [bulkField, setBulkField] = useState("status");
  const [bulkVal, setBulkVal] = useState("Active");

  const load = () => { setLoading(true); api.get("getReferralSources").then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false); }).catch(e => { setErr(e.message); setLoading(false); }); };
  useEffect(() => { load(); }, []);
  // Users are only needed for the owner picker, which only an admin sees.
  useEffect(() => {
    if (gridOn && role.isAdmin && !users.length)
      api.get("getUsers").then(r => setUsers(Array.isArray(r) ? r : [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridOn]);

  // Options come from the DATA, not a hard-coded list, so a value added in SQL
  // is filterable immediately and a value nobody uses doesn't clutter the menu.
  const optsFor = (field) =>
    [...new Set(rows.map(r => (r[field] || "").trim()).filter(Boolean))].sort();

  const matches = (r) =>
    (!statusF || (r.status || "") === statusF) &&
    (!typeF   || (r.contact_type || "") === typeF) &&
    (!ratingF || (r.rating || "") === ratingF) &&
    (!regionF || (r.region || "") === regionF) &&
    (!ownerF  || (ownerF === "__none" ? !r.owner : (r.ownerName || "") === ownerF));

  const preFiltered = rows.filter(matches);
  const activeFilters = [statusF, typeF, ratingF, regionF, ownerF].filter(Boolean).length;
  const unassigned = rows.filter(r => !r.owner).length;
  const ownerOptions = [...(unassigned ? [{ value: "__none", label: `Unassigned (${unassigned})` }] : []),
                        ...optsFor("ownerName")];

  const filtered = localFilter(preFiltered, q, ["company_name", "full_name", "contact_type", "contact_subtype", "region", "ownerName", "status", "email", "phone1", "phone2"]);
  const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, 'company_name', 'asc');
  const { paginated: displayed, page, setPage, totalPages, total, reset } = usePagination(sorted, 100);

  // ── grid helpers ──
  // Filters and sort run on the SAVED values, not the edits, so a row you just
  // set to Inactive doesn't vanish from an "Active" view mid-edit.
  const val = (r, f) => { const e = edits[r.id]; return (e && f in e) ? e[f] : r[f]; };
  const norm = v => (v == null ? "" : String(v).trim());
  const changedFields = (r) => {
    const e = edits[r.id]; if (!e) return [];
    return Object.keys(e).filter(k => norm(e[k]) !== norm(r[k]));
  };
  const isDirty = (r) => changedFields(r).length > 0;
  const dirtyRows = rows.filter(isDirty);
  const setField = (id, f, v) => {
    setEdits(m => ({ ...m, [id]: { ...(m[id] || {}), [f]: v } }));
    if (rowErrs[id]) setRowErrs(m => { const n = { ...m }; delete n[id]; return n; });
  };
  const nameMissing = (r) => !norm(val(r, "company_name")) && !norm(val(r, "firstname")) && !norm(val(r, "lastname"));
  const invalidCount = dirtyRows.filter(nameMissing).length;

  const startGrid = () => { setEdits({}); setRowErrs({}); setSel(new Set()); setGridMsg(null); setGridOn(true); };
  const exitGrid = () => { setEdits({}); setRowErrs({}); setSel(new Set()); setGridOn(false); };
  const cancelGrid = () => dirtyRows.length
    ? setConfirm({ msg: `Discard unsaved changes to ${dirtyRows.length} referral source${dirtyRows.length === 1 ? "" : "s"}?`, onConfirm: exitGrid })
    : exitGrid();

  const visibleIds = sorted.map(r => r.id);
  const allSel = visibleIds.length > 0 && visibleIds.every(id => sel.has(id));
  const toggleAll = () => setSel(allSel ? new Set() : new Set(visibleIds));
  const toggleOne = (id) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const bulkFields = BULK_FIELDS.filter(b => !b.adminOnly || role.isAdmin);
  const applyBulk = () => {
    const ids = [...sel];
    setEdits(m => {
      const n = { ...m };
      ids.forEach(id => { n[id] = { ...(n[id] || {}), [bulkField]: bulkVal }; });
      return n;
    });
    setGridMsg(`Set ${(bulkFields.find(b => b.key === bulkField) || {}).label} on ${ids.length} row${ids.length === 1 ? "" : "s"} — not saved yet.`);
  };

  const saveGrid = async () => {
    setBusy(true); setGridMsg(null);
    try {
      // Only the changed fields go to the server; it writes nothing else.
      const payload = dirtyRows.map(r => {
        const o = { id: r.id };
        changedFields(r).forEach(k => { o[k] = edits[r.id][k]; });
        return o;
      });
      const res = await api.post("saveReferralSourcesBulk", { rows: payload });
      const errs = {};
      (res.errors || []).forEach(e => { if (e.id) errs[e.id] = e.error; });
      // Keep the failed rows' edits open; drop the ones that saved.
      setEdits(m => { const n = {}; Object.keys(errs).forEach(id => { if (m[id]) n[id] = m[id]; }); return n; });
      setRowErrs(errs);
      const nSaved = (res.saved || []).length, nErr = Object.keys(errs).length;
      setGridMsg(nErr ? `Saved ${nSaved}. ${nErr} could not be saved — see the rows marked in red.`
                      : `Saved ${nSaved} referral source${nSaved === 1 ? "" : "s"}.`);
      load();
      if (!nErr) { setSel(new Set()); setGridOn(false); }
    } catch (e) { setGridMsg(e.message || "Save failed"); }
    setBusy(false);
  };

  const ownerOpts = [{ value: "", label: "" }, ...users.map(u => ({ value: u.id, label: u.FullName }))];

  if (loading) return <Spinner />; if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const gridBar = gridOn && (
    <Card style={{ padding: "10px 14px", marginBottom: 12, border: "1.5px solid " + B.cyanDark }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 12px" }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.05em" }}>Grid edit</span>
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{sel.size} selected · Set</span>
        <select value={bulkField} onChange={e => { setBulkField(e.target.value); setBulkVal(e.target.value === "status" ? "Active" : ""); }} style={{ ...cellCss, width: "auto" }}>
          {bulkFields.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
        </select>
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>to</span>
        <span style={{ minWidth: 150 }}>
          {bulkField === "status"          ? <CellSelect value={bulkVal} onChange={setBulkVal} options={RS_OPTIONS.STATUS} />
          : bulkField === "rating"          ? <CellSelect value={bulkVal} onChange={setBulkVal} options={RS_OPTIONS.RATING} />
          : bulkField === "contact_type"    ? <CellSelect value={bulkVal} onChange={setBulkVal} options={RS_OPTIONS.CTYPE} />
          : bulkField === "contact_subtype" ? <CellSelect value={bulkVal} onChange={setBulkVal} options={RS_OPTIONS.CSUBTYPE} />
          : bulkField === "owner"           ? <CellSelect value={bulkVal} onChange={setBulkVal} options={ownerOpts} w={160} />
          : <CellInput value={bulkVal} onChange={setBulkVal} list="rs-regions" />}
        </span>
        <Btn disabled={!sel.size || ((bulkField === "status" || bulkField === "owner") && !bulkVal)} onClick={applyBulk}>
          Apply to {sel.size || ""} selected
        </Btn>

        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {gridMsg && <span style={{ fontSize: 12, color: Object.keys(rowErrs).length ? "#993C1D" : "var(--color-text-secondary)" }}>{gridMsg}</span>}
          {invalidCount > 0 && <span style={{ fontSize: 12, color: "#993C1D" }}>{invalidCount} row{invalidCount === 1 ? "" : "s"} need a company or contact name</span>}
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{dirtyRows.length} changed</span>
          <Btn onClick={cancelGrid} disabled={busy}>Cancel</Btn>
          <Btn variant="primary" onClick={saveGrid} disabled={busy || !dirtyRows.length || invalidCount > 0}>{busy ? "Saving…" : "Save Changes"}</Btn>
        </span>
      </div>
      <datalist id="rs-regions">{optsFor("region").map(r => <option key={r} value={r} />)}</datalist>
    </Card>
  );

  const editRow = (r) => {
    const dirty = isDirty(r), rowErr = rowErrs[r.id], bad = dirty && nameMissing(r);
    const s = (f, options, w) => <CellSelect value={val(r, f)} onChange={v => setField(r.id, f, v)} options={options} w={w} />;
    const t = (f, w, list) => <CellInput value={val(r, f)} onChange={v => setField(r.id, f, v)} w={w} list={list} />;
    const pad = { padding: "5px 6px" };
    return (
      <tr key={r.id} style={{ background: rowErr || bad ? "rgba(153,60,29,0.07)" : dirty ? "rgba(18,181,219,0.06)" : "" }}>
        <Td style={pad}><input type="checkbox" checked={sel.has(r.id)} onChange={() => toggleOne(r.id)} /></Td>
        <Td style={pad}>{t("company_name", 170)}</Td>
        <Td style={pad}>{t("firstname", 90)}</Td>
        <Td style={pad}>{t("lastname", 100)}</Td>
        <Td style={pad}>{t("title", 100)}</Td>
        <Td style={pad}>{s("contact_type", RS_OPTIONS.CTYPE, 110)}</Td>
        <Td style={pad}>{s("contact_subtype", RS_OPTIONS.CSUBTYPE, 110)}</Td>
        <Td style={pad}>{t("region", 90, "rs-regions")}</Td>
        <Td style={pad}>{s("rating", RS_OPTIONS.RATING, 55)}</Td>
        <Td style={pad}>{s("status", RS_OPTIONS.STATUS, 85)}</Td>
        <Td style={pad}>{role.isAdmin && users.length ? s("owner", ownerOpts, 130) : (r.ownerName || "—")}</Td>
        <Td style={pad}>{t("email", 170)}</Td>
        <Td style={pad}>{t("phone1", 110)}</Td>
        <Td style={pad}>{t("city", 100)}</Td>
        <Td style={pad}>{t("state", 45)}</Td>
        <Td style={pad}>
          {rowErr ? <span title={rowErr} style={{ color: "#993C1D", fontSize: 11 }}>✕ {rowErr}</span>
            : bad ? <span style={{ color: "#993C1D", fontSize: 11 }}>Name required</span>
            : dirty ? <span title={"Unsaved: " + changedFields(r).join(", ")} style={{ color: B.cyanDark, fontWeight: 700 }}>●</span> : ""}
        </Td>
      </tr>
    );
  };

  return <>
    <SectionHeader actions={[
      <SearchBar key="s" value={q} onChange={v => { setQ(v); reset(); }} placeholder="Name, company, email, phone..." />,
      role.canEdit && !gridOn ? <Btn key="grid" onClick={startGrid}>Edit Grid</Btn> : null,
      role.canEdit && !gridOn ? <Btn key="add" variant="primary" onClick={() => setModal({ type: "referralsource-add", onDone: load })}>+ Add Referral Source</Btn> : null,
      <ExportBtn key="ex" onClick={() => exportCSV(sorted, ["company_name", "full_name", "contact_type", "contact_subtype", "region", "rating", "status", "ownerName", "lead_count", "opp_count"], "referral_sources.csv")}>Export CSV</ExportBtn>
    ]} />
    {/* ── Filters ── */}
    <Card style={{ padding: "10px 14px", marginBottom: 12 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 14px" }}>
        <FilterSelect hideWhenSingle label="Status" value={statusF} onChange={v => { setStatusF(v); reset(); }}
          options={optsFor("status")} allLabel="Any status" />
        <FilterSelect hideWhenSingle label="Type" value={typeF} onChange={v => { setTypeF(v); reset(); }}
          options={optsFor("contact_type")} allLabel="Any type" />
        <FilterSelect hideWhenSingle label="Rating" value={ratingF} onChange={v => { setRatingF(v); reset(); }}
          options={optsFor("rating")} allLabel="Any rating" />
        <FilterSelect hideWhenSingle label="Region" value={regionF} onChange={v => { setRegionF(v); reset(); }}
          options={optsFor("region")} allLabel="Any region" />
        <FilterSelect hideWhenSingle label="Owner" value={ownerF} onChange={v => { setOwnerF(v); reset(); }}
          options={ownerOptions} allLabel="Any owner" />

        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            {sorted.length} of {rows.length}
          </span>
          {activeFilters > 0 && (
            <Btn onClick={() => { setStatusF(""); setTypeF(""); setRatingF(""); setRegionF(""); setOwnerF(""); reset(); }}>
              Clear filters
            </Btn>
          )}
        </span>
      </div>
    </Card>

    {releaseRec && <ReleaseReferralSourceModal rs={releaseRec} onClose={() => setReleaseRec(null)}
      onEditInstead={() => { const r = releaseRec; setReleaseRec(null); setModal({ type: "referralsource-edit", rec: r, onDone: load }); }}
      onDone={() => { setReleaseRec(null); load(); }} />}
    {gridBar}
    {!gridOn && gridMsg && <div style={{ fontSize: 12, color: "var(--color-text-secondary)", margin: "-4px 0 10px" }}>{gridMsg}</div>}

    <Card style={{ overflowX: "auto" }}>
      {gridOn ? (
        <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
          <thead><tr>
            <Th style={{ width: 28 }}><input type="checkbox" checked={allSel} onChange={toggleAll} title={`Select all ${visibleIds.length} shown`} /></Th>
            <STh label="Company"  sortKey="company_name"    currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="First"    sortKey="firstname"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Last"     sortKey="lastname"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Title"    sortKey="title"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Type"     sortKey="contact_type"    currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Sub-Type" sortKey="contact_subtype" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Region"   sortKey="region"          currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Rating"   sortKey="rating"          currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Status"   sortKey="status"          currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Owner"    sortKey="ownerName"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Email"    sortKey="email"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Phone"    sortKey="phone1"          currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="City"     sortKey="city"            currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="State"    sortKey="state"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <Th></Th>
          </tr></thead>
          <tbody>
            {displayed.map(editRow)}
            {displayed.length === 0 && (
              <tr><Td colSpan={16} style={{ color: "var(--color-text-secondary)" }}>Nothing matches these filters.</Td></tr>
            )}
          </tbody>
        </table>
      ) : (
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
        <thead><tr>
          <STh label="Company"   sortKey="company_name"     currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Contact"   sortKey="full_name"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Type"      sortKey="contact_type"     currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Sub-Type"  sortKey="contact_subtype"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Region"    sortKey="region"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Rating"    sortKey="rating"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Status"    sortKey="status"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Owner"     sortKey="ownerName"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Leads"     sortKey="lead_count"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Opps"      sortKey="opp_count"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <Th></Th>
        </tr></thead>
        <tbody>{displayed.map(r => (
          <tr key={r.id} style={{ cursor: "pointer" }} {...rowNav("referral-source", { referralsourceid: r.id, companyname: r.company_name })}
            onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
            <Td style={{ fontWeight: 500 }}><PageLink page="referral-source" params={{ referralsourceid: r.id, companyname: r.company_name }}>{r.company_name || "—"}</PageLink></Td>
            <Td>{r.full_name || "—"}</Td>
            <Td>{r.contact_type || "—"}</Td>
            <Td>{r.contact_subtype || "—"}</Td>
            <Td>{r.region || "—"}</Td>
            <Td>{r.rating || "—"}</Td>
            <Td><Badge s={r.status} /></Td>
            <Td>{r.owner ? (r.ownerName || "—") : <span style={{ color: "#7A5200", fontStyle: "italic" }}>Unassigned</span>}</Td>
            <TdNum>{r.lead_count || 0}</TdNum>
            <TdNum>{r.opp_count || 0}</TdNum>
            <Td><div style={{ display: "flex", gap: 6, justifyContent:"flex-end" }} onClick={e => e.stopPropagation()}>
              <Btn onClick={() => go("referral-source", { referralsourceid: r.id, companyname: r.company_name })}><Icon type="view" /></Btn>
              {role.canEdit && <Btn onClick={() => setModal({ type: "referralsource-edit", rec: r, onDone: load })}><Icon type="edit" /></Btn>}
              {canRelease(r, currentUser, role) && !role.canDelete &&
                <Btn variant="danger" title="Remove me as owner — asks for a reason first" onClick={() => setReleaseRec(r)}>Remove me</Btn>}
              {role.canDelete && <Btn onClick={() => setConfirm({ msg: "Delete this referral source?", onConfirm: () => delRec("ReferralSources", r.id, "Deleted referral source", load) })} variant="danger"><Icon type="delete" /></Btn>}
            </div></Td>
          </tr>
        ))}
        {displayed.length === 0 && (
          <tr><Td colSpan={11} style={{ color: "var(--color-text-secondary)" }}>
            {activeFilters > 0 ? "Nothing matches these filters." : "No referral sources."}
          </Td></tr>
        )}
        </tbody>
        <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
          <td colSpan={8} style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" }}>Total ({sorted.length})</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{sorted.reduce((s, r) => s + (r.lead_count || 0), 0)}</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{sorted.reduce((s, r) => s + (r.opp_count || 0), 0)}</td>
          <td style={{ borderTop: "1.5px solid var(--color-border-secondary)" }} />
        </tr></tfoot>
      </table>
      )}
      <Pagination page={page} totalPages={totalPages} total={total} pageSize={100} onPage={setPage} />
    </Card>
  </>;
};
